import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { Button, Dropdown, Menu, message, Modal, Space } from 'antd';
import { useRequest } from 'ahooks';
import { useRouter } from 'next/router';
import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  apiSignUpFCRPD,
  apiSignUpFCRPDSingle,
  apiSignUpP,
} from '@/services/exam_signup';
import { DetailContext } from '@/context/detailContext';
import {
  calcExamTitle,
  casLogin,
  findCertInfoByCertId,
  judgeIsPExam,
} from '@/utils/index';
import PCSignupModal from '@/page-components/detail/Intro/pc_signup_modal';
import { apiGetOtherPeoplePay, apiGetPExamInfo } from '@/services/exam_detail';
import { PayState } from '@/page-components/detail/Intro/exam_plan';
import { ExamMethod } from '@/types/index';
import { TCouponItem } from '@/page-components/detail/Intro/Coupon/couponItem';

type TPcSignupBtnP = {
  payState?: PayState;
  isExamed: boolean; // 是否参加过考试
  isPassExam: boolean; // 是否通过了考试，用于fcrpd里判断是否能为自己报名的情况
  className?: string;
  childrenComponent?: ReactNode;
};

type TMustPass = {
  id: string;
  isPass?: boolean;
  label: string;
  value: string;
  price: number;
  student_price: number | null;
  supplierPrice: number | null;
};

const PCSignupBtnP: React.FC<TPcSignupBtnP> = ({
  isPassExam,
  payState,
  isExamed,
  className,
  childrenComponent,
}) => {
  // 从context继承
  const contextProps = useContext(DetailContext);
  const { userInfo, cert_id, data, isHistoryPass } = contextProps;
  const { type } = data;
  const {
    uid = undefined,
    isPassStudentAuthentication,
    isPassRealnameAuthentication,
    isOrganizationAdmin,
    isSupplierState,
  } = userInfo;
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const [isMySelfSignup, setIsMySelfSignup] = useState(true);
  const [signupData, setSignupData] = useState<{
    examDuration: number;
    shouldPrice: number;
    price: number;
    couponPrice: number;
    isAgree: boolean;
    peopleNum: number;
    method: ExamMethod;
    couponInfo: TCouponItem;
    certTypeName: string;
    isPass: boolean;
    premise: string | null;
    mustPassOpt: TMustPass[];
  }>({
    certTypeName: '',
    isPass: false,
    premise: null,
    couponInfo: {
      coupon_name: '',
      coupon_purpose: '',
      coupon_id: 0,
      coupon_log_id: 0,
      coupon_value: 0,
      etime: 0,
    },
    examDuration: 0,
    method: ExamMethod.anytime,
    peopleNum: 1, // 给几个人报名
    shouldPrice: 0, // 选考项的金额总和
    price: 0, // 必选项的金额总和
    isAgree: false,
    couponPrice: 0, // 优惠券的金额总和,
    mustPassOpt: [],
  });

  // 报名P系列考试
  const { runAsync: doSignup } = useRequest(
    (obj) => {
      const fixedData = {
        certId: cert_id,
        pTitle: calcExamTitle(type),
        url: window.location.href,
      };
      if (obj.members) {
        return apiSignUpFCRPD({
          ...fixedData,
          ...obj,
        });
      }
      if (type === 'fcrpd') {
        return apiSignUpFCRPDSingle({
          ...fixedData,
          ...obj,
        });
      }
      return apiSignUpP({
        ...fixedData,
        ...obj,
      });
    },
    {
      manual: true,
      onError: () => {
        message.warning('报名失败，请稍后重试');
      },
      onSuccess: (res) => {
        console.log(res);
        console.log(`cert_id是${cert_id}`);
        if (res.code === 200 && res.success) {
          if (isMySelfSignup) {
            if (res.data?.isSignupExam) {
              message.success('订单创建成功，即将跳转去支付', 2, () => {
                if (res.data.accountOrderId) {
                  window.location.href = `${process.env.NEXT_PUBLIC_PASSPORT_URL}#/orders/payment/${res.data.accountOrderId}`;
                } else {
                  window.location.href = `${process.env.NEXT_PUBLIC_PASSPORT_URL}#/orders`;
                }
              });
              setVisible(false);
            } else {
              message.warning(res.msg || '报名失败，请稍后重试');
            }
          } else {
            message.success('订单创建成功，即将跳转去支付', 2, () => {
              if (res.data.accountOrderId) {
                window.location.href = `${process.env.NEXT_PUBLIC_PASSPORT_URL}#/orders/payment/${res.data.accountOrderId}`;
              } else {
                window.location.href = `${process.env.NEXT_PUBLIC_PASSPORT_URL}#/orders`;
              }
            });
            setVisible(false);
          }
        } else {
          message.warning(res.msg || '报名失败，请稍后重试');
        }
      },
    },
  );
  const signUpExam = async (forMySelf = true) => {
    console.log('点击了');
    if (!uid) {
      Modal.warning({
        title: '请先进行登录',
        content: '当前尚未登录',
        okText: '去登录',
        cancelText: '取消',
        onOk: () => {
          casLogin();
        },
      });
    } else if (!isPassRealnameAuthentication) {
      Modal.confirm({
        icon: <ExclamationCircleOutlined />,
        title: '请先进行初级实名认证',
        content: '认证考试报名仅对实名用户开放，认证后才可报名',
        okText: '立即认证',
        cancelText: '取消',
        onOk: () => {
          window.open(
            `${process.env.NEXT_PUBLIC_PASSPORT_URL}/?tab=auth&type=realname`,
          );
        },
      });
    } else {
      if (forMySelf) {
        setIsMySelfSignup(true);
      } else {
        setIsMySelfSignup(false);
      }
      let otherPayState = 1; // 检查一下是否为他人报名过fcrpd
      if (judgeIsPExam(type)) {
        const res = await apiGetOtherPeoplePay({ certId: cert_id });
        if (res.code === 200 && res.data.isOrder) {
          otherPayState = res.data.payState ? 1 : 0;
        }
      }
      console.log('otherPayState是');
      console.log(otherPayState);
      console.log('payState是');
      console.log(payState);
      if ((payState !== undefined && payState === 0) || otherPayState === 0) {
        Modal.info({
          title: '您已经下过订单了，前往订单中心支付',
          onOk() {
            window.location.href = `${process.env.NEXT_PUBLIC_PASSPORT_URL}/#/orders/`;
          },
        });
        // 如果有前置认证并且前置认证未通过，提示先去前置认证
      } else if (forMySelf && signupData.premise) {
        if (signupData.isPass) {
          setVisible(true);
        } else {
          Modal.confirm({
            icon: <ExclamationCircleOutlined />,
            title: '请先通过前置认证考试',
            content: `报名本认证需要先通过${findCertInfoByCertId(
              signupData.premise,
              'name',
            )}认证`,
            cancelText: '取消',
            okText: '立即认证',
            onOk: () => {
              router.push(`/detail/${signupData.premise}`);
            },
          });
        }
      } else {
        setVisible(true);
      }
    }
  };
  // 恢复组件值到最初
  const initData = () => {
    setSignupData((prev) => ({
      ...prev,
      peopleNum: 1,
      isAgree: false,
      shouldPrice: 0,
      couponPrice: 0,
    }));
  };
  const signup = async (values: any) => {
    if (!signupData.isAgree) {
      message.warning('请勾选同意协议');
    } else {
      console.log('values是');
      console.log(values);
      const newValues = { ...values };
      if (values.examStartTime) {
        newValues.examStartTime = moment(values.examStartTime).valueOf();
      }
      newValues.oprice = signupData.price + signupData.shouldPrice;
      if (signupData?.couponInfo?.coupon_id) {
        newValues.couponLogId = signupData?.couponInfo?.coupon_log_id;
        newValues.couponId = signupData?.couponInfo?.coupon_id;
        newValues.couponPurpose = signupData?.couponInfo?.coupon_purpose;
      }
      if (values.select_user && Array.isArray(values.select_user)) {
        newValues.members = values.select_user.join(',');
        newValues.oprice =
          signupData.peopleNum * (signupData.price + signupData.shouldPrice) -
          signupData.couponPrice;
      }
      console.log('报名数据是');
      console.log(newValues);
      console.log(signupData);
      await doSignup(newValues);
    }
  };
  // 获取p系列考试信息
  const { run: getPExamInfo } = useRequest(
    () => {
      return apiGetPExamInfo(cert_id);
    },
    {
      manual: true,
      debounceWait: 1000,
      onSuccess: (res) => {
        if (res.code === 200 && res.data.list.length > 0) {
          const mustPassOpt = res.data.list.map((listItem) => {
            return {
              id: '',
              label: listItem.examName,
              value: listItem.examid,
              price: listItem.price,
              student_price: listItem.stuPrice,
              supplierPrice: listItem.supplierPrice,
            };
          });
          const examDuration = res.data.list[0].duration;
          setSignupData((prevState) => ({
            ...prevState,
            mustPassOpt,
            method: res.data.method,
            examDuration,
            premise: res.data.premise,
            certTypeName: res.data.certTypeName,
            isPass: res.data.isPass,
          }));
        }
      },
    },
  );
  const calcMoney = (options: TMustPass[]) => {
    let money = 0;
    const calcPrice = (
      price: number,
      student_price: number | null,
      supplierPrice: number | null,
      isPass?: boolean,
    ) => {
      if (isPass) {
        return 0;
      }
      if (isOrganizationAdmin && isSupplierState) {
        /*
         * 为自己报名：供应商管理员显示供应商价格，没有的话显示学生优惠，再没有显示原价
         * 为他人报名：没有供应商价格就显示原价
         * **/
        if (isMySelfSignup) {
          if (supplierPrice) {
            return supplierPrice;
          }
          if (isMySelfSignup && student_price && isPassStudentAuthentication) {
            return student_price;
          }
          return price;
        }
        return supplierPrice || price;
      }
      if (isMySelfSignup && student_price && isPassStudentAuthentication) {
        return student_price;
      }
      return price;
    };
    options.forEach((opt) => {
      money += calcPrice(
        opt.price,
        opt.student_price,
        opt.supplierPrice,
        opt.isPass,
      );
    });
    return money;
  };
  useEffect(() => {
    const money = calcMoney(signupData.mustPassOpt);
    setSignupData((prev) => {
      return { ...prev, price: money };
    });
  }, [signupData.mustPassOpt, visible]);
  useEffect(() => {
    if (judgeIsPExam(type) && signupData.mustPassOpt.length === 0) {
      console.log('获取报名信息接口请求');
      getPExamInfo();
    }
  }, []);

  const createFCRPDBtn = () => {
    if (isOrganizationAdmin) {
      if (isHistoryPass) {
        return (
          <Button
            type="primary"
            className={className}
            onClick={() => {
              signUpExam(false);
            }}
          >
            为他人报名
          </Button>
        );
      }
      if (isPassExam) {
        return (
          <Button
            type="primary"
            className={className}
            onClick={() => {
              signUpExam(false);
            }}
          >
            为他人报名
          </Button>
        );
      }
      return (
        <Dropdown
          overlay={
            <Menu>
              {!isPassExam && (
                <Menu.Item
                  key={Math.random()}
                  onClick={() => {
                    signUpExam();
                  }}
                >
                  <div>为自己报名</div>
                </Menu.Item>
              )}
              <Menu.Item
                key={Math.random()}
                onClick={() => {
                  signUpExam(false);
                }}
              >
                <div>为他人报名</div>
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="primary" className={className}>
            <Space>
              <p>立即报名认证</p>
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      );
    }
    return (
      <Button
        type="primary"
        className={className}
        onClick={() => {
          signUpExam();
        }}
      >
        为自己报名
      </Button>
    );
  };

  return (
    <>
      {childrenComponent ? (
        <div onClick={() => signUpExam()}>{childrenComponent}</div>
      ) : (
        <>
          {createFCRPDBtn()}
          {/*{type === 'fcrpd' ? (*/}
          {/*  <>*/}
          {/*    {createFCRPDBtn()}*/}
          {/*  </>*/}
          {/*) : (*/}
          {/*  <Button*/}
          {/*    className={className}*/}
          {/*    type="primary"*/}
          {/*    onClick={() => signUpExam()}*/}
          {/*  >{isExamed ? '再考一次' : '立即报名认证'}</Button>*/}
          {/*)}*/}
        </>
      )}
      <PCSignupModal
        visible={visible}
        isMySelfSignup={isMySelfSignup}
        onSignupDataChange={(res) => {
          setSignupData((prevState) => {
            return { ...prevState, ...res };
          });
        }}
        onSignUp={async (params) => {
          await signup(params);
        }}
        onCancel={() => {
          initData();
          setVisible(false);
        }}
        signupData={signupData}
      />
    </>
  );
};
export default PCSignupBtnP;
